import { css } from '@emotion/react';
import { useMemo } from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import {
  type ExportImageLegendMode, type ExportImageLocationListMode,
} from '../../types/exportImage/exportImage';

const MAX_SECONDARY_DROPDOWN_TEXT_LENGTH = 30;

const wrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  padding: '10px 30px 20px 30px',
  width: '100%',
  gap: 10,
});

const dropdownStyle = (width?: number) => css({
  width: width || 160,
  flexShrink: 0,
});

const secondaryDropdownStyle = css({
  flex: '1 1 auto',
  minWidth: 0,
});

const dropdownItemStyle = css({
  paddingLeft: 15,
  textTransform: 'capitalize',
});

export type SecondaryDropdownChoiceConfig = Readonly<{
  options: DropdownOption<number>[];
  minimumSelected: number;
  maximumSelected?: number;
}>;

const isSingleSelectSecondaryDropdown = (choice: SecondaryDropdownChoiceConfig) =>
  choice.minimumSelected === choice.maximumSelected
  || (choice.options.length === 1 && choice.minimumSelected === 1);

export type OptionsDropdownComponentProps<T> = Readonly<{
  availableOptions: DropdownOption<T>[];
  selectedOption: T;
  width?: number;
  secondaryChoiceConfig?: SecondaryDropdownChoiceConfig;
  secondarySelection?: number[];

  onSelectOption: (option: T) => void;
  onSecondarySelectionChange?: (selection: number[]) => void;
}>;

export const OptionsDropdownComponent = <T extends ExportImageLegendMode | ExportImageLocationListMode>({
  availableOptions,
  onSecondarySelectionChange,
  onSelectOption,
  secondaryChoiceConfig,
  secondarySelection = [],
  selectedOption,
  width,
}: OptionsDropdownComponentProps<T>) => {

  const secondaryChoicesOptions = useMemo(() => (
    secondaryChoiceConfig?.options.map(option => ({
      ...option,
      name: option.name.length > MAX_SECONDARY_DROPDOWN_TEXT_LENGTH
        ? `${option.name.slice(0, MAX_SECONDARY_DROPDOWN_TEXT_LENGTH - 3)}...`
        : option.name,
    }))
  ), [secondaryChoiceConfig]);

  return (
    <div css={wrapperStyle}>
      <RegularDropdownComponent
        css={dropdownStyle(width)}
        itemStyle={dropdownItemStyle}
        onChange={onSelectOption}
        options={availableOptions}
        triggerStyle={dropdownItemStyle}
        value={selectedOption}
        isDisabled={availableOptions.length < 2}
      />
      {secondaryChoiceConfig && onSecondarySelectionChange && secondaryChoicesOptions && (
        isSingleSelectSecondaryDropdown(secondaryChoiceConfig) ? (
          <RegularDropdownComponent
            css={secondaryDropdownStyle}
            itemStyle={dropdownItemStyle}
            triggerStyle={dropdownItemStyle}
            options={secondaryChoicesOptions}
            value={secondarySelection[0] ?? null}
            onChange={value => onSecondarySelectionChange([value])}
            isDisabled={secondaryChoicesOptions.length < 2}
          />
        ) : (
          <AutocompleteComponent
            css={secondaryDropdownStyle}
            maxSelectedVisible={1}
            multiple
            checkSelected
            options={secondaryChoicesOptions}
            value={secondarySelection}
            onChange={onSecondarySelectionChange}
            selectionLimit={secondaryChoiceConfig.maximumSelected}
          />
        )
      )}
    </div>
  );
};
